import type { UploadResult } from "@msuite/utils/types";
import { createContext, useContext, useEffect, useRef } from "react";
import { useLoading } from "../hooks";
import type { FileUploadController } from "./use-file-upload";

/** Types */
interface FileUploadCollectorRef {
	map: Map<string, FileUploadController>;
}

export type FileUploadCollector = ReturnType<typeof useFileUploadCollector>;

interface IFileUploadCollectorContext {
	ref: React.RefObject<FileUploadCollectorRef>;
}

/** Hooks */
export function useFileUploadCollector() {
	const loader = useLoading();

	const ref = useRef<FileUploadCollectorRef>({
		map: new Map(),
	});

	async function handleOnBatchUpload() {
		try {
			loader.start();

			const resultMap: Map<string, UploadResult[]> = new Map();

			const promises = [];

			for (const [identifier, controller] of ref.current?.map.entries() || []) {
				promises.push(
					(async () => {
						const results = await controller.handleOnFileUpload();
						resultMap.set(identifier, results);
					})(),
				);
			}

			await Promise.all(promises);
			loader.stop();
			return resultMap;
		} catch (error) {
			console.error(error);
			loader.stop();
		}
	}

	return { ref, loader, isUploading: loader.isLoading, handleOnBatchUpload };
}

export function useRegisterFileUploadController(
	identifier: string,
	controller: FileUploadController,
) {
	const context = useContext(FileUploadCollectorContext);

	useEffect(() => {
		context.ref.current?.map.set(identifier, controller);
		return () => {
			context.ref.current?.map.delete(identifier);
		};
	}, [identifier, controller]);
}

export function useFileUploadCollectorContext() {
	return useContext(FileUploadCollectorContext);
}

/** Context */
const FileUploadCollectorContext = createContext<IFileUploadCollectorContext>(
	{} as IFileUploadCollectorContext,
);

export function FileUploadCollectorProvider({
	children,
	collector,
}: {
	children: React.ReactNode;
	collector: FileUploadCollector;
}) {
	/** Render */
	return (
		<FileUploadCollectorContext.Provider value={{ ref: collector.ref }}>
			{children}
		</FileUploadCollectorContext.Provider>
	);
}
